import { dataService } from '../../data';
import { languageService } from '@unpispas/upp-base';

import { DataObject, ObjectOptions } from '../base';
import { CreateObject } from '../item';

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

const _schema = {
    name: 'I18NVALUE',
    fields: [
        { name: 'ES', type: 'string' },
        { name: 'EN', type: 'string' },
        { name: 'EU', type: 'string' },
        { name: 'ZH', type: 'string' },
    ],
    relate: [],
} as const;

const _I18nValueClass = CreateObject(_schema);
if (!_I18nValueClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

/****************************/
/* CLASS DECLARATION        */
/****************************/

/**
 * Persistent translations for a single translatable field (for example, a product name).
 *
 * Table **`I18NVALUE`**. Each row stores one text in up to four languages. Owner entities
 * (product, category, option, …) reference their own row through a relation, the same way
 * they reference an {@link Address}: one dedicated instance per field, not a shared pool.
 *
 * **Stored values** — schema columns `ES`, `EN`, `EU`, `ZH` (getters/setters generated by
 * {@link CreateObject}). Use them in edition forms: read and write the language the user is
 * editing, without fallback.
 *
 * **Display values** — lowercase getters `es`, `en`, `eu`, `zh`. Each returns the text for
 * that language when present; otherwise the first non-null value following
 * {@link languageService.GetLanguages}. Pick the getter that matches the context (UI language,
 * ticket language, …).
 *
 * @example
 * ```typescript
 * // Edition: raw column
 * nameRow.ES = 'Caña';
 *
 * // Cart in English
 * label = nameRow.en;
 *
 * // Ticket when the place default language is Spanish
 * label = nameRow.es;
 * ```
 */
export type I18nValues = Partial<Record<string, string | null>>;

/** Resolves display text from a frozen i18n map via an ephemeral {@link I18nValue}. */
export function I18nTr(data: dataService, values: I18nValues | null, lang: string): string | null {
    if (!values){
        return null;
    }

    const _row = new I18nValue(null, data, { volatile: true });
    _row.values = values;
    return _row.tr(lang);
}

export class I18nValue extends _I18nValueClass {
    /**
     * @param objid - Row identifier, or `null` for a new unsaved row.
     * @param data - Application data service (storage, sync, injector).
     * @param objoptions - Optional object lifecycle flags; defaults to `{}`.
     */
    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null){
        super(_schema, objid, data, objoptions || {});
    }

    /**
     * Deep-copies this row and its tracked relations.
     *
     * @param store - Objects already copied in this operation, to avoid duplicates.
     * @returns A new `I18nValue` instance with the same field values.
     */
    Copy(store: Array<DataObject> = []): I18nValue {
        return this._Copy(store) as I18nValue;
    }

    private get lang(): languageService {
        return this.data.injector.get(languageService);
    }

    /**
     * Stored columns for supported languages, both for reading and writing the full map.
     *
     * Reading iterates {@link languageService.GetLanguages}; only non-null values are
     * included. Writing assigns each supported column from the map (trimmed; missing or
     * empty entries become `null`) through the generated setters, so the change is tracked
     * (`ToUpdate`) like any other field. The setter is the symmetric counterpart of the
     * getter: `row.values = row.values` is a no-op for stored text.
     *
     * @returns A JSON-safe partial map, or `null` when every supported column is {@code null}.
     */
    get values(): I18nValues | null {
        const _map: I18nValues = {};
        for (const _lang of this.lang.GetLanguages()) {
            const _text = this[_lang];
            if (_text !== null) {
                _map[_lang] = _text;
            }
        }
        return Object.keys(_map).length > 0 ? _map : null;
    }

    set values(map: I18nValues | null) {
        for (const _lang of this.lang.GetLanguages()) {
            this[_lang] = (map?.[_lang] ?? '').toString().trim() || null;
        }
    }

    /**
     * First non-null stored translation following {@link languageService.GetLanguages}.
     *
     * @returns The fallback text, or `null` when every column is `null`.
     * @internal
     */
    private get _fallback(): string | null {
        for (const _lang of this.lang.GetLanguages()){
            const _str = this[_lang];
            if (_str !== null){
                return _str;
            }
        }
        return null;
    }

    /**
     * Spanish text for display.
     *
     * Returns {@link I18nValue.ES} when set; otherwise the first available translation in
     * {@link languageService.GetLanguages} order.
     */
    get es(): string | null {
        return this.ES || this._fallback;
    }

    /**
     * English text for display.
     *
     * Returns {@link I18nValue.EN} when set; otherwise the first available translation in
     * {@link languageService.GetLanguages} order.
     */
    get en(): string | null {
        return this.EN || this._fallback;
    }

    /**
     * Basque text for display.
     *
     * Returns {@link I18nValue.EU} when set; otherwise the first available translation in
     * {@link languageService.GetLanguages} order.
     */
    get eu(): string | null {
        return this.EU || this._fallback;
    }

    /**
     * Chinese text for display.
     *
     * Returns {@link I18nValue.ZH} when set; otherwise the first available translation in
     * {@link languageService.GetLanguages} order.
     */
    get zh(): string | null {
        return this.ZH || this._fallback;
    }

    /**
     * Display text for the active UI language, or for an explicit language when provided.
     *
     * Resolves the language through {@link languageService} on the data injector unless
     * `lang` is set; then uses the matching display getter (`es`, `en`, `eu`, `zh`).
     *
     * @param lang - Optional language code (`ES`, `EN`, …); defaults to the current UI language.
     */
    tr(lang: string | null = null): string | null {
        return this[(lang ?? this.lang.GetLanguage()).toLowerCase()];
    }
}
